import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

declare global {
  interface Window {
    // What index.html took out of the address before analytics could read it
    forgeLinkSecrets?: Record<string, string>;
  }
}

const storageKey = (name: string) => `forge.${name}`;

/** Drops a spent value, so a later visit without a link shows the missing-link state. */
export const forgetLinkSecret = (name: string) => {
  delete window.forgeLinkSecrets?.[name];
  try {
    sessionStorage.removeItem(storageKey(name));
  } catch {
    // Storage is off; there is nothing to forget.
  }
};

/**
 * Reads a one-time value (a checkout session id, a password link) from the link once. index.html
 * takes it out of the address before analytics loads; one that arrives by navigating inside the app
 * is dropped from the address bar here. The tab keeps it in sessionStorage, so a reload still works,
 * until forgetLinkSecret says it is spent.
 */
const useLinkSecret = (name: string) => {
  const [params, setParams] = useSearchParams();
  const fromUrl = params.get(name);

  const [value] = useState(() => {
    const fromLink = fromUrl ?? window.forgeLinkSecrets?.[name] ?? null;
    try {
      if (fromLink) {
        sessionStorage.setItem(storageKey(name), fromLink);
        return fromLink;
      }
      return sessionStorage.getItem(storageKey(name));
    } catch {
      return fromLink;
    }
  });

  useEffect(() => {
    if (fromUrl) {
      setParams({}, { replace: true });
    }
  }, [fromUrl, setParams]);

  return value;
};

export default useLinkSecret;
